/* eslint-disable no-console -- logger 工具内部允许 console 调试输出 */
/**
 * @fileoverview 日志钩子系统
 *
 * 钩子用途：当特定 action 的日志被上报时，自动执行钩子函数，将其返回值合并到日志 data 中。
 * 典型场景：为卡顿/错误日志自动注入业务上下文（文档ID、块数量、编辑器状态等），
 * 而不必在每个上报点重复传这些字段。
 *
 * 支持同步/异步钩子，多个钩子的返回数据自动合并。
 */
import type { LogActionHook, LogItem } from './core/types'

/**
 * 钩子系统
 * @description 由 AliWebLogger 持有一个实例。core 在 send 时调用 {@link run}
 * 收集额外数据并合并到 LogItem.data。
 */
export class HookSystem {
  /** key = action，value = 该 action 的钩子数组 */
  private hooks: Map<string | number, LogActionHook[]> = new Map()

  constructor(private isDev = false) {}

  /**
   * 执行指定 action 的所有钩子，收集并合并返回数据
   * @param action 日志 action
   * @param logData 当前日志数据（只读，钩子不应修改）
   * @returns 合并后的额外字段（空对象表示无钩子或钩子无返回）
   */
  async run(action: string | number, logData: Readonly<LogItem>): Promise<Record<string, unknown>> {
    const hooks = this.hooks.get(action)
    if (!hooks || hooks.length === 0) return {}

    const extraDataList: Record<string, unknown>[] = []
    for (const hook of hooks) {
      try {
        const result = await Promise.resolve(hook(logData, action))
        if (result && typeof result === 'object') {
          extraDataList.push(result)
        }
      } catch (error) {
        // 钩子失败不影响主日志上报，仅告警
        console.error(`[🚨Logger] Hook execution failed for action "${action}":`, error)
      }
    }
    return Object.assign({}, ...extraDataList)
  }

  /**
   * 订阅指定 action 的日志钩子
   * @param action 单个或多个 action
   * @param hook 钩子函数
   * @param description 订阅描述（仅 dev 打印，便于调试）
   * @returns 取消订阅函数（同时取消所有传入 action 的订阅）
   */
  on(action: string | number | Array<string | number>, hook: LogActionHook, description?: string): () => void {
    const actions = Array.isArray(action) ? action : [action]
    const unsubscribers: Array<() => void> = []

    for (const act of actions) {
      if (!this.hooks.has(act)) {
        this.hooks.set(act, [])
      }
      this.hooks.get(act)!.push(hook)

      if (this.isDev && description) {
        console.log(`[🎣Logger] Hook registered for action "${act}": ${description}`)
      }
      unsubscribers.push(() => this.off(act, hook))
    }

    return () => unsubscribers.forEach(unsub => unsub())
  }

  /** 取消订阅指定 action 的某个钩子 */
  off(action: string | number | Array<string | number>, hook: LogActionHook): void {
    const actions = Array.isArray(action) ? action : [action]
    for (const act of actions) {
      const hooks = this.hooks.get(act)
      if (!hooks) continue

      const index = hooks.indexOf(hook)
      if (index !== -1) {
        hooks.splice(index, 1)
        if (this.isDev) {
          console.log(`[🎣Logger] Hook removed for action "${act}"`)
        }
      }
      if (hooks.length === 0) {
        this.hooks.delete(act)
      }
    }
  }

  /** 清除指定 action 的所有钩子，不传则清除全部 */
  clear(action?: string | number | Array<string | number>): void {
    if (action === undefined) {
      this.hooks.clear()
      if (this.isDev) {
        console.log('[🎣Logger] All hooks cleared')
      }
      return
    }
    const actions = Array.isArray(action) ? action : [action]
    for (const act of actions) {
      this.hooks.delete(act)
      if (this.isDev) {
        console.log(`[🎣Logger] All hooks cleared for action "${act}"`)
      }
    }
  }

  /** 是否存在某 action 的钩子 */
  has(action: string | number): boolean {
    const hooks = this.hooks.get(action)
    return !!hooks && hooks.length > 0
  }
}
